/** POST /ingest/requests takes three things; the server owns everything else. */

/** A YouTube channel id, the only identifier the service accepts as a source. */
export const CHANNEL_ID_RE = /^UC[\w-]{22}$/

/** The service's statuses (2026_0015.pgsql CHECK), with our words for them. */
export const REQUEST_STATUSES = Object.freeze({
  queued: { label: 'Waiting to start', tone: 'pending' },
  running: { label: 'In progress', tone: 'running' },
  needs_attention: { label: 'Needs attention', tone: 'warn' },
  needs_review: { label: 'Needs a decision', tone: 'warn' },
  completed: { label: 'Done', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
})

const SETTLED = new Set(['completed', 'cancelled'])
// Stopped, and only a person restarts them - so stop polling too.
const WAITING_ON_US = new Set(['needs_attention', 'needs_review'])

/**
 * A request stopped by a person can still finish. store.finish always writes
 * status='completed', and cancel_requested stays true, so a green "Done" is
 * the one thing the row is not.
 */
export function wasStopped(request) {
  return Boolean(request) && request.status === 'completed' && Boolean(request.cancel_requested)
}

/**
 * Both take the whole request, not a status string: a stopped request IS
 * `completed`, so a status alone cannot answer either question.
 */
export function statusLabel(request) {
  if (wasStopped(request)) return 'Stopped'
  const status = (request && request.status) || ''
  return (REQUEST_STATUSES[status] && REQUEST_STATUSES[status].label) || status.replace(/_/g, ' ')
}

export function statusTone(request) {
  if (wasStopped(request)) return 'muted'
  const status = (request && request.status) || ''
  return (REQUEST_STATUSES[status] && REQUEST_STATUSES[status].tone) || 'muted'
}

export function isSettled(status) {
  return SETTLED.has(status)
}

export function needsAPerson(status) {
  return WAITING_ON_US.has(status)
}

/**
 * The idempotency key. Replaying one returns the request it already created,
 * so it is generated once per attempt and reused across retries - but the
 * server rejects a replay carrying different input, so it must be re-minted
 * when the source or force_refresh changes.
 */
export function newClientKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  // jsdom and older Safari. Only has to be unique, not unguessable.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

export function buildCreateBody({ source, clientKey, forceRefresh = false }) {
  return { source, client_key: clientKey, force_refresh: Boolean(forceRefresh) }
}

/**
 * `requested_by` is an admin f_id, never a name (see useAdminUserNames.js).
 * The viewer's own name is always free (AuthContext's own account fetch), so
 * "my own request" never depends on the admin-roster call at all; anyone
 * else's name depends on that call succeeding, and returns null (never the
 * id) when it has not - the caller decides the raw-id fallback, so this stays
 * a plain lookup with no opinion about what to show instead.
 */
export function requesterName(requestedBy, { currentUser, adminNames } = {}) {
  if (!requestedBy) return null
  if (currentUser && currentUser.f_id && currentUser.f_id === requestedBy && currentUser.name) return currentUser.name
  return (adminNames && adminNames.get(requestedBy)) || null
}
